import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Container, Divider, Grid, Stack } from "@mui/material";

import {
  ConfirmActionModal,
  ActionModal,
} from "../../components/Modals/ActionModal";

import { AssetTag, CreateAsset, PageActions } from "../../config/types";

import { ExpandableContainer } from "../../components/Collapsible/ExpandableContainer";
import ItemInput from "../../components/Input/ItemInput";
import { FormSchemaType, useDmsForm } from "../../hooks/useDmsFrom";
import { BatchSelectInput } from "../../common-ui/select-input-models/BatchSelectInput";
import { Field } from "../../utils/formSchema";
import { AssetCategorySelectInput } from "../../common-ui/select-input-models/AssetCategorySelectInput";
import { assetApi } from "../../services/assets/asset-crud";
import { AssetCategoryApi } from "../../services/assets/assetCategory";
import { AssetTagApi } from "../../services/assets/assetTag";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import { MultiSelectInput } from "../../components/Input/MultiSelectInput";
import { usePageModels } from "../../hooks/usePageActions";
import { DamsAppAsset } from "../../assets/registry";
import { DatePicker } from "../../components/calender";
import VerticalGap from "../../components/Gap/VerticalGap";
import {
  ASSET_OWNERSHIP_LIST,
  ASSET_STATUS_LIST,
  ASSET_BRAND_NAME,
} from "../../config/constants/common";
import dayjs from "dayjs";
import appRoutePath from "../../config/path";
import FileUpload from "../../components/file-upload/FileUpload";
import LocalStorageService from "../../store/localStorage";
import { alertDialogStore } from "../../store/dialogs";

const initialState: CreateAsset = {
  serial_number: "",
  embossment_number: "",
  model: "",
  brand: "",
  description: "",
  warranty_in_months: 0,
  asset_status: "functional",
  ownership: "official",
  batch_id: "",
  asset_category_id: "",
  manufactured_date: "",
  date_received: "",
  // asset_image_url: "",
  meta: [],
};

function AddAssetPage() {
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploadError, setFileUploadError] = useState<boolean>(false);
  const { batch_id } = useLocation().state || {};
  const {
    form,
    updateForm,
    updateInputField,
    updateNestedArrayWithInputField,
    fieldHasError,
    setSubmitAttempt,
    onFormSubmit,
  } = useDmsForm<CreateAsset>({ ...initialState, batch_id: batch_id ?? "" });
  const [assetTagList, setTagList] = useState<AssetTag[]>([]);

  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();
  const [isProcessing, setProcess] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMeta = async () => {
      if (form.asset_category_id.isValid && form.asset_category_id.value) {
        const res = await AssetCategoryApi.getAssetCategoryTags(
          form.asset_category_id.value,
        );
        if (res.data) {
          let tags = res.data;

          const enrichedTags = await Promise.all(
            tags.map(async (tag): Promise<AssetTag> => {
              try {
                const allTagsRes = await AssetTagApi.getAssetTags();
                if (allTagsRes.data && Array.isArray(allTagsRes.data)) {
                  const matchedTag = allTagsRes.data.find(
                    (t) => t.name === tag.name,
                  );
                  if (matchedTag) {
                    return {
                      ...tag,
                      asset_tag_field_type: matchedTag.asset_tag_field_type as
                        | "text-input"
                        | "number-input"
                        | "single-select"
                        | "multi-select",
                      asset_tag_options: matchedTag.asset_tag_options,
                    };
                  }
                }
              } catch (error) {}
              return tag as AssetTag;
            }),
          );

          setTagList(enrichedTags);
          updateForm((p) => ({
            ...p,
            meta: {
              isValid: false,
              errorMessage: "field required",
              value: enrichedTags.map((tag) => {
                return {
                  asset_category_tag_id: tag.asset_category_tag_id,
                  meta_value: "",
                };
              }),
            },
          }));
        }
      } else {
        setTagList([]);
      }
    };
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.asset_category_id]);

  useEffect(() => {
    const prevFormState =
      LocalStorageService.get<FormSchemaType<CreateAsset>>("ASSET_FORM");
    if (prevFormState) {
      updateForm({
        ...prevFormState,
        asset_category_id: {
          value: "",
          isValid: false,
          errorMessage: "required",
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cancelAddItem() {
    setUnsavedChanges(true);
  }

  function onSaveItemClick() {
    openModel("add");
    updatePageAction({
      type: "add",
    });
  }

  async function confirmUserSave() {
    const excludeField: (keyof CreateAsset)[] = [
      "meta",
      "description",
      "embossment_number",
      "manufactured_date",
      "warranty_in_months",
    ];
    setSubmitAttempt(true);
    const { data, isValid } = onFormSubmit({ excludeFields: excludeField });
    if (isValid) {
      setProcess(true);
      const response = await assetApi.createAsset(data);
      setProcess(false);
      updatePageAction({
        type: pageAction?.type,
        message: response.message,
        title: pageAction?.type === "edit" ? "Update Asset" : "New Asset",
        success: response.success,
      });
      openModel("alert");
    }
  }

  function onSeeAllPress() {
    navigate(appRoutePath.ASSET_REGISTRY_HISTORY_PAGE);
  }

  function onAddNewPress() {
    closeModel();
  }

  function discardChanges() {
    setUnsavedChanges(false);
    LocalStorageService.remove("ASSET_FORM");
    navigate(appRoutePath.ASSET_REGISTRY_HISTORY_PAGE);
  }

  function saveChanges() {
    LocalStorageService.save("ASSET_FORM", form);
    setUnsavedChanges(false);

    alertDialogStore.open({
      title: "Save your changes",
      subtitle: "current changes saved on device",
      onLeftBtnPress: () => {},
      onRightBtnPress: discardChanges,
      isSuccess: false,
      rightBtnTxt: "Continue",
      leftBtnTxt: "Close",
    });
  }

  function handleBatchSelect(item: Field) {
    updateForm((p) => ({ ...p, batch_id: item }));
  }

  function handleAssetCatSelect(item: Field) {
    updateForm((p) => ({ ...p, asset_category_id: item }));
  }

  const handleManufacturedDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      manufactured_date: { value: date, isValid: true, errorMessage: "" },
    }));
  };

  const handleDateReceivedPicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      date_received: { value: date, isValid: true, errorMessage: "" },
    }));
  };

  const renderDynamicAssetTagInput = (tag: AssetTag, index: number) => {
    const fieldType = tag.asset_tag_field_type;
    const metaValue = form.meta.value[index]?.meta_value ?? "";
    const handleMetaChange = updateNestedArrayWithInputField<
      CreateAsset,
      "meta"
    >("meta", index, "meta_value");

    const options = Array.isArray(tag.asset_tag_options)
      ? tag.asset_tag_options
      : tag.asset_tag_options
        ? [tag.asset_tag_options]
        : [];

    switch (fieldType) {
      case "text-input":
        return (
          <ItemInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Enter ${tag.name}`}
            value={metaValue}
            handleChange={handleMetaChange}
            required
          />
        );

      case "number-input":
        return (
          <ItemInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Enter ${tag.name}`}
            value={metaValue}
            handleChange={handleMetaChange}
            type="number"
            required
          />
        );

      case "single-select":
        const singleSelectOptions = options.map((option) => ({
          label: option,
          value: option,
        }));
        return (
          <ItemSelectInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Select ${tag.name}`}
            items={singleSelectOptions}
            item=""
            value={metaValue}
            handleChange={handleMetaChange}
            required
          />
        );

      case "multi-select":
        const multiSelectOptions = options.map((option) => ({
          label: option,
          value: option,
        }));
        const multiSelectValue = Array.isArray(metaValue)
          ? metaValue
          : metaValue
            ? metaValue.split(",").map((v) => v.trim())
            : [];
        return (
          <MultiSelectInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Select ${tag.name}`}
            items={multiSelectOptions}
            value={multiSelectValue}
            handleChange={(selectedValues) =>
              handleMetaChange({
                target: { value: selectedValues.join(",") },
              } as any)
            }
            required
          />
        );

      default:
        return (
          <ItemInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Enter ${tag.name} detail`}
            value={metaValue}
            handleChange={handleMetaChange}
            required
          />
        );
    }
  };

  {
    /* const handleFile = (files: File[] | File | null) => {
  if (files) {
    const file = Array.isArray(files) ? files[0] : files;
    setSelectedFile(file);
    setFileUploadError(false);
  } else {
    setSelectedFile(null);
    setFileUploadError(true);
  }
  };
  */
  }

  return (
    <MainPageBase
      title={"Add New Asset"}
      pageState={"add-item"}
      cancelBtnClick={cancelAddItem}
      addItemBtnOnClick={onSaveItemClick}
    >
      <Box height={`100%`} display={"flex"} justifyContent={"center"}>
        <Box width={"100%"} m={"0px 32px"}>
          <Box>
            {/* asset information  */}
            <Stack direction={"row"} gap={4}>
              <ExpandableContainer
                initOpenState={true}
                label={"Asset Information"}
              >
                <Container>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 3 }}
                    columns={{ xs: 4, sm: 8, md: 12 }}
                  >
                    <Grid item xs={2} sm={6} md={4}>
                      <ItemSelectInput
                        label="Asset Status"
                        placeholder="Select Asset Status"
                        items={ASSET_STATUS_LIST}
                        item=""
                        value={form.asset_status.value}
                        error={fieldHasError("asset_status")}
                        handleChange={updateInputField("asset_status")}
                        errorMessage={form.asset_status.errorMessage}
                        required={true}
                      />
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <ItemSelectInput
                        label="Ownership"
                        placeholder="Select Ownership"
                        items={ASSET_OWNERSHIP_LIST}
                        item=""
                        value={form.ownership.value}
                        error={fieldHasError("ownership")}
                        handleChange={updateInputField("ownership")}
                        errorMessage={form.ownership.errorMessage}
                        required={true}
                      />
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <AssetCategorySelectInput
                        placeholder="Select Asset Category"
                        label="Asset Category"
                        handleChange={handleAssetCatSelect}
                        error={fieldHasError("asset_category_id")}
                        required={true}
                      />
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <BatchSelectInput
                        size="short"
                        value={form.batch_id.value}
                        placeholder="Select batch Id"
                        label="Batch"
                        handleChange={handleBatchSelect}
                        error={fieldHasError("batch_id")}
                        required={true}
                      />
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <ItemInput
                        label="Embossment Number"
                        placeholder="Enter Embossment Number"
                        value={form.embossment_number?.value}
                        handleChange={updateInputField("embossment_number")}
                      />
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <Box width={260}>
                        <DatePicker
                          handleOnchange={handleDateReceivedPicker}
                          value={dayjs(form.date_received.value)}
                          label="Date Received"
                          error={fieldHasError("date_received")}
                          errorMessage={form.date_received.errorMessage}
                          required={true}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <Box width={260}>
                        <DatePicker
                          handleOnchange={handleManufacturedDatePicker}
                          value={dayjs(form.manufactured_date?.value)}
                          label="Manufactured Date "
                          error={fieldHasError("manufactured_date")}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <ItemInput
                        label="Serial Number"
                        placeholder="Enter Serial Number"
                        value={form.serial_number.value}
                        error={fieldHasError("serial_number")}
                        handleChange={updateInputField("serial_number")}
                        errorMessage={form.serial_number.errorMessage}
                        required={true}
                      />
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <ItemSelectInput
                        label="Brand Name"
                        placeholder="Select Asset Brand"
                        items={ASSET_BRAND_NAME}
                        item=""
                        value={form.brand.value}
                        error={fieldHasError("brand")}
                        handleChange={updateInputField("brand")}
                        errorMessage={form.brand.errorMessage}
                        required={true}
                      />
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <ItemInput
                        label="Model"
                        placeholder="Enter model Detail"
                        value={form.model.value}
                        error={fieldHasError("model")}
                        handleChange={updateInputField("model")}
                        errorMessage={form.model.errorMessage}
                        required={true}
                      />
                    </Grid>

                    <Grid item xs={2} sm={6} md={4}>
                      <ItemInput
                        label="Warranty (in Months)"
                        placeholder="Warranty (in Months)"
                        value={form.warranty_in_months?.value}
                        error={fieldHasError("warranty_in_months")}
                        handleChange={updateInputField(
                          "warranty_in_months",
                          "number",
                        )}
                      />
                    </Grid>

                    {/* <Grid item xs={2} sm={6} md={4}>
                      <FileUpload 
                        label="Upload Asset Image"
                        size="short"
                        multiple={false} 
                        accept="image/jpeg,image/jpg,.jpg,.jpeg" 
                        maxFileSize={2 * 1024 * 1024}
                        onFileSelect={handleFile}
                        error={fileUploadError}
                        errorMessage="Please select a valid JPG or JPEG image"
                      />
                    </Grid> */}
                  </Grid>

                  <VerticalGap height={30} />
                </Container>
              </ExpandableContainer>
            </Stack>

            <Divider variant="fullWidth" sx={{ marginTop: "20px" }} />

            {/* Additional information  */}
            <Stack direction={"row"} gap={4}>
              {/* <SimpleLabel title="Additional Information" /> */}
              <ExpandableContainer
                label="Additional Information"
                initOpenState={true}
              >
                <Container sx={{ marginBottom: "25px" }}>
                  {assetTagList.length > 0 ? (
                    <Grid
                      container
                      spacing={{ xs: 2, md: 3 }}
                      columns={{ xs: 4, sm: 8, md: 12 }}
                    >
                      {assetTagList.map((tag, index) => (
                        <Grid item xs={2} sm={6} md={4} key={index}>
                          {renderDynamicAssetTagInput(tag, index)}
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight={200}
                      fontFamily={"Inter"}
                      fontWeight={400}
                    >
                      <span>
                        Select an Asset Category to view additional information
                        fields
                      </span>
                    </Box>
                  )}
                </Container>
              </ExpandableContainer>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Modals  */}
      {/* succesfully saved user  */}
      <ActionModal
        show={models.showAlertModel}
        title={
          pageAction?.success === false ? "Failed to Add Asset" : "Asset Added"
        }
        subtitle={pageAction?.message ?? ""}
        leftIcon={
          pageAction?.success === false ? DamsAppAsset.icons.warning : undefined
        }
        onLeftBtnPress={() => onSeeAllPress()}
        leftBtnTxt={"See All"}
        onRightBtnPress={() => onAddNewPress()}
        rightBtnTxt={pageAction?.type === "add" ? "Add New" : "continue"}
        onClosePress={closeModel}
      />

      {/* confirm user save */}
      <ConfirmActionModal
        show={models.showAddModel}
        title={"Add Asset"}
        subtitle={`You are adding  a new Asset. Click "Confirm" to Add`}
        onRightBtnPress={() => confirmUserSave()}
        onRightBtnLoading={isProcessing}
        rightBtnTxt={"Confirm"}
        onLeftBtnPress={closeModel}
        onClosePress={closeModel}
        leftBtnTxt={"Cancel"}
      />

      {/* confirm unsaved changes */}
      <ConfirmActionModal
        show={unsavedChanges}
        title={"Unsaved Changes"}
        subtitle={`Do you want to save changes for your asset or discard them?`}
        onRightBtnPress={() => saveChanges()}
        rightBtnTxt={"Save Changes"}
        onLeftBtnPress={() => discardChanges()}
        leftBtnTxt={"Discard"}
        onClosePress={() => setUnsavedChanges(false)}
      />
    </MainPageBase>
  );
}

export { AddAssetPage };
